import React, { useEffect, useState } from 'react';
import axios from '../utils/api';
import { Modal, Button } from 'react-bootstrap';
import '../styles/Reports.css';

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [allReports, setAllReports] = useState([]);
  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMessage, setSelectedMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isSendingFollowUp, setIsSendingFollowUp] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [followUpTarget, setFollowUpTarget] = useState(null);
  
  const PER_PAGE = 100;

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const res = await axios.get('/auth/reports/');
        setAllReports(res.data);
        setReports(res.data);
        setFiltered(res.data);
      } catch (err) {
        console.error(err);
        alert('Failed to load reports');
      }
    };
    fetchReports();
  }, []);

  // Get current page data
  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * PER_PAGE;
    const endIndex = startIndex + PER_PAGE;
    return filtered.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const currentPageData = getCurrentPageData();

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearch(term);
    
    if (!term.trim()) {
      setFiltered(allReports);
      setCurrentPage(1);
      return;
    }
    
    const filteredReports = allReports.filter(r => {
      // Search in sender name
      const matchesSenderName = r.full_name?.toLowerCase().includes(term);
      
      // Search in sender email
      const matchesSenderEmail = r.user_email?.toLowerCase().includes(term);
      
      // Search in sol number
      const matchesSolNumber = r.sol_number?.toLowerCase().includes(term);
      
      // Search in date
      const matchesDate = new Date(r.created_at).toLocaleString().toLowerCase().includes(term);
      
      // Search in message
      const matchesMessage = r.message?.toLowerCase().includes(term);
      
      // Search in recipient emails (check each email in the array)
      const matchesRecipients = r.emails?.some(email => email.toLowerCase().includes(term));
      
      // Search in attachments (check each attachment in the array)
      const matchesAttachments = r.attached_files?.some(file => file.toLowerCase().includes(term));
      
      return matchesSenderName || matchesSenderEmail || matchesSolNumber || 
             matchesDate || matchesMessage || matchesRecipients || matchesAttachments;
    });
    
    setFiltered(filteredReports);
    setCurrentPage(1);
  };

  const exportCSV = () => {
    const rows = [
      ['Date', "SolNumber", "Sender", 'Recipients', 'Files', 'Message'],
      ...filtered.map(r => [
        new Date(r.created_at).toLocaleString(),
        r.sol_number,
        `${r.full_name} <${r.user_email}>`,
        r.emails.join(', '),
        r.attached_files.join(', '),
        r.message.replace(/\n/g, ' ')
      ])
    ];
    const csv = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'broadcast_reports.csv';
    link.click();
  };

  const openMessageModal = (message) => {
    setSelectedMessage(message);
    setShowModal(true);
  };

  const closeMessageModal = () => {
    setShowModal(false);
    setSelectedMessage('');
  };

  const openFollowUpModal = (report) => {
    setFollowUpTarget(report);
    setFollowUpMessage('');
    setShowFollowUpModal(true);
  };

  const closeFollowUpModal = () => {
    setShowFollowUpModal(false);
    setFollowUpTarget(null);
    setFollowUpMessage('');
  };

  const sendFollowUp = async () => {
    if (!followUpMessage.trim()) return alert('Message is required');
    setIsSendingFollowUp(true);
    try {
      await axios.post(`/auth/reports/${followUpTarget.id}/follow-up/`, {
        message: followUpMessage,
      });
      alert('Follow-up sent!');
      closeFollowUpModal();
      const res = await axios.get('/auth/reports/');
      setAllReports(res.data);
      setReports(res.data);
      setFiltered(res.data);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      alert('Failed to send follow-up.');
    } finally {
      setIsSendingFollowUp(false);
    }
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <div>
          <h1>Broadcast Reports</h1>
          <p>View and manage all your email broadcasts</p>
        </div>
        <button onClick={exportCSV} className="btn btn-success">
          <i className="fas fa-download"></i>
          Export CSV
        </button>
      </div>

      <div className="search-bar">
        <div className="search-input-wrapper">
          <i className="fas fa-search"></i>
          <input
            type="text"
            className="search-input"
            placeholder="Search across all fields..."
            value={search}
            onChange={handleSearch}
          />
        </div>
        <div className="results-count">
          Showing {currentPageData.length} of {filtered.length} results (Page {currentPage} of {totalPages})
        </div>
      </div>

      <div className="reports-table-container">
        <table className="reports-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Sol #</th>
              <th>Sender</th>
              <th>Recipients</th>
              <th>Attachments</th>
              <th>Message</th>
              <th>Follow-ups</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentPageData.map((r, i) => (
              <tr key={i}>
                <td>{new Date(r.created_at).toLocaleDateString()}</td>
                <td><span className="badge-badge-primary">{r.sol_number}</span></td>
                <td>
                  <div className="sender-info">
                    <strong>{r.full_name}</strong>
                    <span>{r.user_email}</span>
                  </div>
                </td>
                <td>
                  <div className="recipients-list">
                    {r.emails.slice(0, 2).map((e, idx) => (
                      <span key={idx} className="badge-badge-primary-small">{e}</span>
                    ))}
                    {r.emails.length > 2 && (
                      <span className="badge-badge-secondary">+{r.emails.length - 2}</span>
                    )}
                  </div>
                </td>
                <td>
                  {r.attached_files.length > 0 ? (
                    <div className="files-badges">
                      {r.attached_files.slice(0, 2).map((f, idx) => (
                        <span key={idx} className="badge-badge-secondary-small" title={f}>
                          <i className="fas fa-paperclip"></i>
                          {f.length > 15 ? f.substring(0, 15) + '...' : f}
                        </span>
                      ))}
                      {r.attached_files.length > 2 && (
                        <span className="badge-badge-secondary">+{r.attached_files.length - 2}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted">None</span>
                  )}
                </td>
                <td>
                  <div className="message-preview">
                    {r.message.length > 60 ? `${r.message.slice(0, 60)}...` : r.message}
                    {r.message.length > 60 && (
                      <button className="link-btn" onClick={() => openMessageModal(r.message)}>
                        View Full
                      </button>
                    )}
                  </div>
                </td>
                <td>
                  <span className="followup-count">{r.follow_up_count}</span>
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => openFollowUpModal(r)}
                  >
                    <i className="fas fa-reply"></i>
                    Follow-up
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <p>No reports found</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination-container">
          <button 
            className="btn btn-outline-primary"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            <i className="fas fa-chevron-left"></i>
            Previous
          </button>
          
          <div className="pagination-info">
            <span>Page {currentPage} of {totalPages}</span>
          </div>
          
          <button 
            className="btn btn-outline-primary"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Next
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      <Modal show={showModal} onHide={closeMessageModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Full Message</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: 'pre-wrap' }}>
          {selectedMessage}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeMessageModal}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFollowUpModal} onHide={closeFollowUpModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Send Follow-Up</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ marginBottom: '16px' }}>
            Sending to: <strong>{followUpTarget?.emails?.join(', ')}</strong>
          </p>
          <textarea
            className="form-control"
            rows="8"
            placeholder="Type your follow-up message..."
            value={followUpMessage}
            onChange={(e) => setFollowUpMessage(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeFollowUpModal}>Cancel</Button>
          <Button variant="primary" onClick={sendFollowUp} disabled={isSendingFollowUp}>
            {isSendingFollowUp ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Sending...
              </>
            ) : 'Send Follow-Up'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Reports;
